import { useState, useEffect, useCallback } from 'react'
import {
  Card, Row, Col, Tabs, Table, Tag, Button, Space, Select, Typography, Statistic, Divider
} from 'antd'
import {
  SecurityScanOutlined,
  CarOutlined,
  UsergroupAddOutlined,
  TeamOutlined,
  ExportOutlined,
  AlertOutlined,
  DashboardOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography

const API = ''

// ── Types ─────────────────────────────────────────────────────────────────────

interface TodaySummary {
  total: number
  entered: number
  at_weighbridge: number
  unloading: number
  completed: number
  voided: number
}

interface GateEvent {
  id: string
  inward_no: string
  entry_time: string
  gate_direction: string
  gate_category: string
  entry_type: string
  vehicle_number: string
  supplier_name?: string
  reference_type?: string
  reference_id?: string
  status: string
  driver_name?: string
}

interface Visitor {
  id: string
  visitor_name: string
  purpose: string
  host_name?: string
  company_name?: string
  in_time: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function directionTag(dir: string) {
  return dir === 'INWARD'
    ? <Tag color="green">INWARD</Tag>
    : <Tag color="orange">OUTWARD</Tag>
}

function categoryTag(cat: string) {
  const colors: Record<string, string> = {
    MATERIAL: 'blue',
    DISPATCH: 'purple',
    VISITOR: 'cyan',
    CONTRACTOR: 'geekblue',
    SCRAP: 'gold',
    SERVICE: 'magenta',
  }
  return <Tag color={colors[cat] ?? 'default'}>{cat}</Tag>
}

function statusTag(s: string) {
  const colors: Record<string, string> = {
    ENTERED: 'processing',
    COMPLETED: 'success',
    VOIDED: 'error',
    UNLOADING: 'warning',
    GROSS_WEIGHED: 'orange',
    TARE_WEIGHED: 'orange',
  }
  return <Tag color={colors[s] ?? 'default'}>{s}</Tag>
}

function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch {
    return iso
  }
}

// ── Quick Action cards ────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { title: 'New Inward Truck', subtitle: 'Record a truck arriving at gate', icon: <CarOutlined style={{ fontSize: 28, color: '#1677ff' }} />, path: '/gate/entries' },
  { title: 'Register Visitor', subtitle: 'Log a visitor entry/exit', icon: <UsergroupAddOutlined style={{ fontSize: 28, color: '#13c2c2' }} />, path: '/gate/visitors' },
  { title: 'Contractor Entry', subtitle: 'Record contractor labor entry', icon: <TeamOutlined style={{ fontSize: 28, color: '#722ed1' }} />, path: '/security/contractor-labor' },
  { title: 'Create Outward Pass', subtitle: 'Raise a material outward pass', icon: <ExportOutlined style={{ fontSize: 28, color: '#fa8c16' }} />, path: '/security/material-outward' },
  { title: 'View Dispatch Status', subtitle: 'Check FG dispatch orders', icon: <SecurityScanOutlined style={{ fontSize: 28, color: '#52c41a' }} />, path: '/sales/dispatch' },
  { title: 'Log Incident', subtitle: 'Report a security incident', icon: <AlertOutlined style={{ fontSize: 28, color: '#f5222d' }} />, path: '/security/incidents' },
]

// ── Main Component ────────────────────────────────────────────────────────────

export default function GateDashboardPage() {
  const navigate = useNavigate()
  const { plantId, setPlantId, plants } = usePlantId()

  const [summary, setSummary] = useState<TodaySummary | null>(null)
  const [events, setEvents] = useState<GateEvent[]>([])
  const [visitors, setVisitors] = useState<Visitor[]>([])
  const [visitorCount, setVisitorCount] = useState(0)

  const [dirFilter, setDirFilter] = useState<string>('')
  const [catFilter, setCatFilter] = useState<string>('')

  const [loadingEvents, setLoadingEvents] = useState(false)

  // ── Fetch summary ────────────────────────────────────────────────────────

  const fetchSummary = useCallback(async () => {
    if (!plantId) return
    try {
      const res = await apiClient.get(`${API}/gate-entries/today-summary`, { params: { plant_id: plantId } })
      setSummary(res.data)
    } catch {
      // silently fail — dashboard still usable
    }
  }, [plantId])

  // ── Fetch unified events ─────────────────────────────────────────────────

  const fetchEvents = useCallback(async () => {
    if (!plantId) return
    setLoadingEvents(true)
    try {
      const params: Record<string, string | number> = { plant_id: plantId, size: 100 }
      if (dirFilter) params.gate_direction = dirFilter
      if (catFilter) params.gate_category = catFilter
      const res = await apiClient.get(`${API}/gate-entries/unified-events`, { params })
      setEvents(res.data?.items ?? [])
    } catch {
      setEvents([])
    } finally {
      setLoadingEvents(false)
    }
  }, [plantId, dirFilter, catFilter])

  // ── Fetch inside visitors ────────────────────────────────────────────────

  const fetchVisitors = useCallback(async () => {
    if (!plantId) return
    try {
      const res = await apiClient.get(`${API}/visitors/inside`, { params: { plant_id: plantId } })
      const data = res.data?.items ?? res.data ?? []
      setVisitors(Array.isArray(data) ? data : [])
      setVisitorCount(Array.isArray(data) ? data.length : (res.data?.total ?? 0))
    } catch {
      setVisitors([])
      setVisitorCount(0)
    }
  }, [plantId])

  useEffect(() => {
    fetchSummary()
    fetchEvents()
    fetchVisitors()
  }, [fetchSummary, fetchEvents, fetchVisitors])

  // ── Table columns ────────────────────────────────────────────────────────

  const eventColumns = [
    {
      title: 'Entry No',
      dataIndex: 'inward_no',
      key: 'inward_no',
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{v}</span>,
    },
    {
      title: 'Time',
      dataIndex: 'entry_time',
      key: 'entry_time',
      render: (v: string) => fmtTime(v),
      width: 70,
    },
    {
      title: 'Direction',
      dataIndex: 'gate_direction',
      key: 'gate_direction',
      render: directionTag,
      width: 100,
    },
    {
      title: 'Category',
      dataIndex: 'gate_category',
      key: 'gate_category',
      render: categoryTag,
      width: 110,
    },
    {
      title: 'Type',
      dataIndex: 'entry_type',
      key: 'entry_type',
      render: (v: string) => <Tag>{v}</Tag>,
      width: 120,
    },
    {
      title: 'Vehicle / Person',
      dataIndex: 'vehicle_number',
      key: 'vehicle_number',
      render: (v: string, row: GateEvent) => (
        <span>{v}{row.driver_name ? ` / ${row.driver_name}` : ''}</span>
      ),
    },
    {
      title: 'Supplier / Reference',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render: (v: string, row: GateEvent) => v || row.reference_type || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: statusTag,
      width: 120,
    },
  ]

  const visitorColumns = [
    { title: 'Name', dataIndex: 'visitor_name', key: 'visitor_name' },
    { title: 'Company', dataIndex: 'company_name', key: 'company_name', render: (v: string) => v || '—' },
    { title: 'Purpose', dataIndex: 'purpose', key: 'purpose' },
    { title: 'Host', dataIndex: 'host_name', key: 'host_name', render: (v: string) => v || '—' },
    {
      title: 'In Time',
      dataIndex: 'in_time',
      key: 'in_time',
      render: (v: string) => fmtTime(v),
    },
  ]

  // ── Derived counts for summary cards ─────────────────────────────────────

  const activeCount = summary ? (summary.entered + summary.at_weighbridge + summary.unloading) : 0

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row align="middle" gutter={16} style={{ marginBottom: 24 }}>
        <Col>
          <SecurityScanOutlined style={{ fontSize: 28, color: '#1677ff' }} />
        </Col>
        <Col flex={1}>
          <Title level={3} style={{ margin: 0 }}>Gate Control Center</Title>
        </Col>
        <Col>
          <Select
            style={{ width: 200 }}
            placeholder="Select Plant"
            value={plantId || undefined}
            onChange={setPlantId}
            options={plants.map((p: any) => ({ value: p.id, label: p.plant_name || p.name }))}
          />
        </Col>
      </Row>

      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Card>
            <Statistic
              title="Total Entries Today"
              value={summary?.total ?? 0}
              valueStyle={{ color: '#1677ff' }}
              prefix={<DashboardOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Card>
            <Statistic
              title="Currently Active"
              value={activeCount}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Card>
            <Statistic
              title="Completed"
              value={summary?.completed ?? 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Card>
            <Statistic
              title="Voided / Rejected"
              value={summary?.voided ?? 0}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Card>
            <Statistic
              title="Visitors Inside"
              value={visitorCount}
              valueStyle={{ color: '#722ed1' }}
              prefix={<UsergroupAddOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs
        defaultActiveKey="events"
        items={[
          {
            key: 'events',
            label: 'All Gate Events Today',
            children: (
              <>
                <Space style={{ marginBottom: 16 }}>
                  <span>Direction:</span>
                  {['', 'INWARD', 'OUTWARD'].map((d) => (
                    <Button
                      key={d || 'ALL'}
                      type={dirFilter === d ? 'primary' : 'default'}
                      size="small"
                      onClick={() => setDirFilter(d)}
                    >
                      {d || 'ALL'}
                    </Button>
                  ))}
                  <Divider type="vertical" />
                  <span>Category:</span>
                  {['', 'MATERIAL', 'DISPATCH', 'VISITOR', 'CONTRACTOR', 'SCRAP'].map((c) => (
                    <Button
                      key={c || 'ALL'}
                      type={catFilter === c ? 'primary' : 'default'}
                      size="small"
                      onClick={() => setCatFilter(c)}
                    >
                      {c || 'ALL'}
                    </Button>
                  ))}
                </Space>
                <Table
                  rowKey="id"
                  dataSource={events}
                  columns={eventColumns}
                  loading={loadingEvents}
                  size="small"
                  pagination={{ pageSize: 50 }}
                  scroll={{ x: 900 }}
                />
              </>
            ),
          },
          {
            key: 'quick-actions',
            label: 'Quick Actions',
            children: (
              <Row gutter={[16, 16]}>
                {QUICK_ACTIONS.map((action) => (
                  <Col key={action.path} xs={24} sm={12} md={8} lg={6} xl={4}>
                    <Card
                      hoverable
                      style={{ textAlign: 'center', minHeight: 160 }}
                      actions={[
                        <Button
                          type="link"
                          key="open"
                          onClick={() => navigate(action.path)}
                        >
                          Open
                        </Button>,
                      ]}
                    >
                      <div style={{ marginBottom: 8 }}>{action.icon}</div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{action.title}</div>
                      <div style={{ color: '#888', fontSize: 12 }}>{action.subtitle}</div>
                    </Card>
                  </Col>
                ))}
              </Row>
            ),
          },
        ]}
      />

      {/* Currently Inside Visitors */}
      {visitors.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <Title level={5}>Currently Inside Visitors</Title>
          <Table
            rowKey="id"
            dataSource={visitors}
            columns={visitorColumns}
            size="small"
            pagination={false}
            scroll={{ x: 700 }}
          />
        </div>
      )}
    </div>
  )
}
